import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  CurrencyEuroIcon,
  TagIcon,
  ArrowDownIcon,
  ArrowUpIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../../utils/apiClient';
import ConfirmationModal from '../ConfirmationModal';

interface QuickBookingDocument {
  id: number;
  name: string;
  invoice_amount?: string;
  invoice_date?: string;
  ai_comment?: string;
}

interface QuickBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  document: QuickBookingDocument | null;
}

interface Category {
  id: number;
  name: string;
}

interface Account {
  id: number;
  name: string;
  kontotyp: string;
}

const QuickBookingModal: React.FC<QuickBookingModalProps> = ({ isOpen, onClose, onSuccess, document: doc }) => {
  const [type, setType] = useState<'deposit' | 'withdrawal'>('withdrawal');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [description, setDescription] = useState('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    type: 'success' | 'warning' | 'error' | 'question' | 'info';
    title: string;
    message: string;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && doc) {
      fetchData();
      prefillFromDocument(doc);
      const timer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [isOpen, doc]);

  const parseDateFromName = (name: string): string => {
    // Try patterns: 2026-01-15, 15.01.2026, 15-01-2026, 20260115
    const isoMatch = name.match(/(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

    const deMatch = name.match(/(\d{2})\.(\d{2})\.(\d{4})/);
    if (deMatch) return `${deMatch[3]}-${deMatch[2]}-${deMatch[1]}`;

    const deDashMatch = name.match(/(\d{2})-(\d{2})-(\d{4})/);
    if (deDashMatch) return `${deDashMatch[3]}-${deDashMatch[2]}-${deDashMatch[1]}`;

    const compactMatch = name.match(/(\d{4})(\d{2})(\d{2})/);
    if (compactMatch) {
      const m = parseInt(compactMatch[2]);
      const d = parseInt(compactMatch[3]);
      if (m >= 1 && m <= 12 && d >= 1 && d <= 31) {
        return `${compactMatch[1]}-${compactMatch[2]}-${compactMatch[3]}`;
      }
    }

    return new Date().toISOString().split('T')[0];
  };

  const prefillFromDocument = (doc: QuickBookingDocument) => {
    // Amount
    if (doc.invoice_amount && parseFloat(doc.invoice_amount) > 0) {
      setAmount(parseFloat(doc.invoice_amount).toFixed(2));
      setType('withdrawal');
    } else {
      setAmount('');
    }

    // Date from invoice_date or document name
    if (doc.invoice_date) {
      const d = new Date(doc.invoice_date);
      if (!isNaN(d.getTime())) {
        setDate(d.toISOString().split('T')[0]);
      } else {
        setDate(parseDateFromName(doc.name));
      }
    } else {
      setDate(parseDateFromName(doc.name));
    }

    // Description
    setDescription(doc.name.replace(/\.\w+$/, ''));
    setCategoryId('');
    setAccountId('');
  };

  const fetchData = async () => {
    try {
      const [categoriesRes, accountsRes] = await Promise.all([
        apiClient.get('/api/finance/categories'),
        apiClient.get('/api/finance/accounts'),
      ]);
      setCategories(categoriesRes.data);
      setAccounts(accountsRes.data);
    } catch {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Daten konnten nicht geladen werden.',
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!doc) return;

    if (!amount || !date) {
      setConfirmModal({ isOpen: true, type: 'error', title: 'Pflichtfelder', message: 'Betrag und Datum sind erforderlich.' });
      return;
    }
    if (!categoryId) {
      setConfirmModal({ isOpen: true, type: 'error', title: 'Kategorie fehlt', message: 'Bitte Kategorie auswählen.' });
      return;
    }
    if (!accountId) {
      setConfirmModal({ isOpen: true, type: 'error', title: 'Konto fehlt', message: 'Bitte Konto auswählen.' });
      return;
    }

    setLoading(true);
    try {
      // 1. Create booking
      const formData = new FormData();
      formData.append('type', type);
      formData.append('amount', Math.abs(parseFloat(amount)).toString());
      formData.append('date', date);
      formData.append('category_id', categoryId);
      formData.append('account_id', accountId);
      formData.append('description', description || '');

      const bookingRes = await apiClient.post('/api/finance/bookings', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const buchungId = bookingRes.data?.buchung_id;

      // 2. Link document to booking
      if (buchungId) {
        await apiClient.post(`/api/finance/statements/${buchungId}/documents`, {
          document_ids: [doc.id],
        });
      }

      setConfirmModal({
        isOpen: true,
        type: 'success',
        title: 'Buchung erstellt',
        message: 'Buchung wurde erstellt und mit dem Dokument verknüpft.',
      });

      setTimeout(() => {
        handleClose();
        if (onSuccess) onSuccess();
      }, 1500);
    } catch (err: any) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: err.response?.data?.error || 'Buchung konnte nicht erstellt werden.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose(), 300);
  };

  if (!isOpen || !doc) return null;

  return (
    <>
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
      onClick={handleClose}
    >
      <div
        className="w-full sm:max-w-lg bg-white dark:bg-[#1c2432] border-t sm:border border-gray-200 dark:border-gray-700 rounded-t-2xl sm:rounded-2xl max-h-[90vh] flex flex-col shadow-2xl animate-slide-up sm:animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
          {/* Header */}
          <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="w-9 h-9 rounded-xl bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-400 grid place-items-center flex-shrink-0">
              <CurrencyEuroIcon className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-bold text-gray-900 dark:text-white truncate">Buchung erstellen</h3>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{doc.name}</p>
            </div>
            <button
              onClick={handleClose}
              className="w-9 h-9 grid place-items-center rounded-lg hover:bg-gray-100 dark:hover:bg-white/5 text-gray-500 dark:text-gray-400 flex-shrink-0"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden px-5 py-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Typ */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                  Typ
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setType('withdrawal')}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      type === 'withdrawal'
                        ? 'bg-error-50 dark:bg-error-900/20 border-error-500 text-error-700 dark:text-error-300'
                        : 'bg-gray-50 dark:bg-[#0f1419] border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <ArrowUpIcon className="h-5 w-5 mx-auto mb-1 text-error-600" />
                    <span className="text-xs font-medium">Auszahlung</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setType('deposit')}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      type === 'deposit'
                        ? 'bg-success-50 dark:bg-success-900/20 border-success-500 text-success-700 dark:text-success-300'
                        : 'bg-gray-50 dark:bg-[#0f1419] border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <ArrowDownIcon className="h-5 w-5 mx-auto mb-1 text-success-600" />
                    <span className="text-xs font-medium">Einzahlung</span>
                  </button>
                </div>
              </div>

              {/* Betrag und Datum */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Betrag <span className="text-error-500">*</span>
                  </label>
                  <div className="relative">
                    <CurrencyEuroIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <input
                      type="number"
                      step="0.01"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      required
                      placeholder="0,00"
                      className="w-full pl-10 pr-3 py-2.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white placeholder-gray-400"
                      disabled={loading}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Datum <span className="text-error-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    required
                    className="w-full px-3 py-2.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white"
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Kategorie */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Kategorie <span className="text-error-500">*</span>
                </label>
                <div className="relative">
                  <TagIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <select
                    value={categoryId}
                    onChange={(e) => setCategoryId(e.target.value)}
                    required
                    className="w-full pl-10 pr-3 py-2.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white appearance-none"
                    disabled={loading}
                  >
                    <option value="">Kategorie wählen</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Konto */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Konto <span className="text-error-500">*</span>
                </label>
                <select
                  value={accountId}
                  onChange={(e) => setAccountId(e.target.value)}
                  required
                  className="w-full px-3 py-2.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white appearance-none"
                  disabled={loading}
                >
                  <option value="">Konto wählen</option>
                  {accounts.map((acc) => (
                    <option key={acc.id} value={acc.id}>{acc.name}</option>
                  ))}
                </select>
              </div>

              {/* Beschreibung */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Beschreibung
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Verwendungszweck"
                  className="w-full px-3 py-2.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white placeholder-gray-400 resize-none"
                  rows={2}
                  disabled={loading}
                />
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700/50 mt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-5 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-all font-medium"
                  disabled={loading}
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={loading || !categoryId || !accountId}
                  className="px-5 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Wird erstellt...' : 'Buchung erstellen'}
                </button>
              </div>
            </form>
          </div>
      </div>
    </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        visible={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant="single"
        showCloseButton={true}
        button={{
          text: 'OK',
          onPress: () => setConfirmModal({ ...confirmModal, isOpen: false }),
        }}
      />
    </>
  );
};

export default QuickBookingModal;
